import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as React from "react";

import { buttonVariants } from "../../../components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../../../components/ui/command";
import MultipleSelector, { type Option } from "../../../components/ui/multiselect";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { cn } from "../../../handlers/cn.utils";

interface DropdownProps {
	value?: string | string[];
	placeholder: string;
	onChange: (value: string) => void;
	options: string[];
	multiple?: boolean;
	search?: boolean;
	id?: string;
	name?: string;
	disabled?: boolean;
	onBlur?: () => void;
	"aria-invalid"?: React.AriaAttributes["aria-invalid"];
	"aria-describedby"?: string;
	"aria-labelledby"?: string;
}

function Dropdown({
	value,
	placeholder,
	onChange,
	options,
	multiple = false,
	search = false,
	id,
	name,
	disabled,
	onBlur,
	"aria-invalid": ariaInvalid,
	"aria-describedby": ariaDescribedBy,
	"aria-labelledby": ariaLabelledBy,
}: DropdownProps) {
	const [open, setOpen] = React.useState(false);
	const selectorOptions = React.useMemo<Option[]>(
		() => options.map((option) => ({ label: option, value: option })),
		[options],
	);

	if (multiple) {
		const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
		const selectedOptions = selectedValues.map((option) => ({ label: option, value: option }));

		return (
			<MultipleSelector
				commandProps={{ "aria-invalid": ariaInvalid }}
				defaultOptions={selectorOptions}
				disabled={disabled}
				emptyIndicator={<p>No results found.</p>}
				hideClearAllButton
				hidePlaceholderWhenSelected
				inputProps={{
					id,
					name,
					onBlur,
					readOnly: !search,
					"aria-invalid": ariaInvalid,
					"aria-describedby": ariaDescribedBy,
					"aria-labelledby": ariaLabelledBy,
				}}
				onChange={(nextOptions) => {
					const nextValues = nextOptions.map((option) => option.value);
					const changedValue =
						nextValues.find((option) => !selectedValues.includes(option)) ??
						selectedValues.find((option) => !nextValues.includes(option));

					if (changedValue) onChange(changedValue);
				}}
				placeholder={placeholder}
				value={selectedOptions}
			/>
		);
	}

	const selectedValue = typeof value === "string" ? value : "";

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				id={id}
				name={name}
				type="button"
				role="combobox"
				aria-expanded={open}
				aria-invalid={ariaInvalid}
				aria-describedby={ariaDescribedBy}
				aria-labelledby={ariaLabelledBy}
				disabled={disabled}
				onBlur={onBlur}
				className={cn(buttonVariants({ variant: "outline", size: "sm" }), "w-full justify-between")}
			>
				<span className={cn("truncate", !selectedValue && "text-muted-foreground/70")}>
					{selectedValue || placeholder}
				</span>
				<ChevronsUpDownIcon aria-hidden="true" className="size-4 shrink-0 opacity-50" />
			</PopoverTrigger>
			<PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
				<Command>
					{search ? <CommandInput placeholder="Search" /> : null}
					<CommandList>
						<CommandEmpty>No results found.</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option}
									value={option}
									onSelect={() => {
										onChange(option);
										setOpen(false);
									}}
								>
									<CheckIcon aria-hidden="true" className={cn("size-4", selectedValue !== option && "invisible")} />
									{option}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

export default Dropdown;
